import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

import { trackPurchase } from "../lib/analytics";

/**
 * Stripe return-URL polling for parts orders.
 * Stripe redirects to `/parts/thank-you?session_id={CHECKOUT_SESSION_ID}` on success.
 * We pull the order ID from sessionStorage (stashed at cart-checkout time) so we
 * know which order to poll.
 */
export default function PartsThankYouPage() {
  const [params] = useSearchParams();
  const [phase, setPhase] = useState("polling");
  const [order, setOrder] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const sid = params.get("session_id");
    const oid = params.get("order_id") || sessionStorage.getItem("parts-last-order");
    if (!sid || !oid) {
      setPhase("unknown");
      return;
    }
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/orders/${oid}/payment-status`, { params: { session_id: sid } });
        if (r.data?.payment_status === "paid") {
          const ord = await api.get(`/orders/${oid}`).catch(() => null);
          const fullOrder = ord?.data || { order_number: r.data.order_number };
          setOrder(fullOrder);
          // Fire GA4 purchase event for the parts order. Items map → cart_items
          // when present so we get per-line-item attribution in GA4.
          trackPurchase({
            orderNumber: fullOrder.order_number,
            value: fullOrder?.pricing?.total ?? r.data?.amount_total,
            currency: "USD",
            items: (fullOrder?.cart_items || []).map((ci) => ({
              sku: ci.sku || ci.slug,
              name: ci.name,
              price: ci.price,
              quantity: ci.quantity,
              category: ci.category || "parts",
            })),
          });
          setPhase("paid");
          window.history.replaceState({}, "", "/parts/thank-you");
          sessionStorage.removeItem("parts-last-order");
          return;
        }
        if (r.data?.status === "expired") { setPhase("expired"); return; }
      } catch { /* transient */ }
      if (attempts >= 8) { setPhase("unclear"); return; }
      setTimeout(tick, 2000);
    };
    tick();
  }, [params]);

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] flex items-center justify-center px-5">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[var(--brand-border)] p-8 text-center">
        {phase === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin brand-text" />
            <h1 className="text-xl font-bold mt-4 text-[var(--brand-text)]">Confirming your payment…</h1>
            <p className="text-sm muted-text mt-1">Hang tight.</p>
          </>
        )}
        {phase === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto brand-text" />
            <h1 className="text-2xl font-extrabold mt-4 text-[var(--brand-text)]">Order received!</h1>
            <p className="text-sm muted-text mt-1">
              Order <strong>{order?.order_number || "—"}</strong> is in. We'll email a receipt
              and ping you when it's ready{order?.fulfillment === "delivery" ? " for delivery" : " for pickup"}.
            </p>
            <Link to="/parts" className="cta-btn mt-6 w-full inline-block">Back to parts</Link>
            <Link to="/lookup" className="block text-xs muted-text underline mt-3">
              Check order status anytime
            </Link>
          </>
        )}
        {(phase === "unclear" || phase === "expired" || phase === "unknown") && (
          <>
            <AlertCircle className="w-10 h-10 mx-auto text-amber-500" />
            <h1 className="text-xl font-bold mt-4 text-[var(--brand-text)]">
              {phase === "expired" ? "Session expired" : "We couldn't confirm yet"}
            </h1>
            <p className="text-sm muted-text mt-1">
              If you completed payment, check your email for the receipt — it'll arrive within a minute or two.
            </p>
            <Link to="/parts" className="cta-btn mt-6 w-full inline-block">Back to parts</Link>
          </>
        )}
      </div>
    </div>
  );
}
